import React from 'react';
import { Link } from 'react-router-dom';
import PipelineAnimation from './SolutionAnimations/PipelineAnimation';
import EvidentAnimation from './SolutionAnimations/EvidentAnimation';
import Container from './shared/Container';

const CheckIcon = () => (
  <svg className="h-4 w-4 text-vermillion-500 flex-shrink-0 mt-0.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
  </svg>
);

const solutions = [
  {
    id: "intake",
    tag: "Document Intelligence",
    name: "Vermillion Intake",
    tagline: "From any document to trusted data.",
    stats: [
      { value: "98%+", label: "Field accuracy" },
      { value: "80%+", label: "Automation rate" },
    ],
    description: "Documents arrive in every format: typed forms, handwritten records, multi-page files. Vermillion Intake classifies, extracts, and verifies each one with accuracy that holds over time, not just at deployment.",
    features: [
      "Classifies any document type automatically on arrival",
      "Extracts structured fields with 98%+ accuracy, including handwritten content",
      "Uncertain extractions pass through a verified review workflow. Not skipped, never guessed.",
      "Performance monitored continuously with client-visible reports, weekly to quarterly",
    ],
    learnMore: "/products/intake",
    Component: PipelineAnimation,
    isReversed: false,
  },
  {
    id: "evident",
    tag: "Decision Automation",
    name: "Vermillion Evident",
    tagline: "Any decision guideline, made executable.",
    stats: [
      { value: "100%", label: "Deterministic" },
      { value: "60%+", label: "Decisions automated" },
    ],
    description: "Decision logic lives in policy documents, guidelines, and operational SOPs. Read differently by every person, applied inconsistently at scale. Evident converts any decision description into deterministic, executable programs, verified by your own experts, so every decision follows the same logic and every answer is auditable.",
    features: [
      "Converts governing documents into deterministic, executable decision logic",
      "Identical inputs produce identical outcomes. Every decision is fully traceable to its governing rule.",
      "Human experts verify each encoded rule before it handles live decisions",
      "Describe rule changes in plain language. The system proposes, validates, and deploys on approval.",
    ],
    learnMore: "/products/evident",
    Component: EvidentAnimation,
    isReversed: true,
  },
];

const Solutions = () => {
  return (
    <section id="solutions" className="py-16 sm:py-24 bg-white overflow-x-hidden">
      <Container>

        <div className="text-center mb-16 lg:mb-24">
          <p className="inline-block text-xs font-semibold tracking-[0.12em] uppercase text-vermillion-600 mb-4">
            Our Products
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
            Purpose-built AI for Operations Teams
          </h2>
          <p className="mt-4 text-lg text-gray-500 max-w-2xl mx-auto leading-relaxed">
            Not general-purpose AI retrofitted for one industry. Tools built from the ground up for complex documents, workflows, and accuracy standards.
          </p>
        </div>

        <div className="flex flex-col gap-20 lg:gap-40">
          {solutions.map((solution) => (
            <div
              id={solution.id}
              key={solution.id}
              className="block w-full lg:grid lg:grid-cols-12 lg:gap-12 lg:items-center"
              style={{ scrollMarginTop: '6rem' }}
            >
              {/* Text column */}
              <div className={`lg:col-span-5${solution.isReversed ? ' lg:order-2' : ''}`}>

                {/* Tag + name */}
                <p className="text-xs font-semibold tracking-[0.12em] uppercase text-vermillion-600 mb-3">
                  {solution.tag}
                </p>
                <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
                  {solution.name}
                </h3>
                <p className="mt-1 text-base font-medium text-gray-400 italic">
                  {solution.tagline}
                </p>

                {/* Stats */}
                <div className="mt-6 flex gap-8">
                  {solution.stats.map((stat, i) => (
                    <div key={i} className={i > 0 ? 'border-l border-gray-200 pl-8' : ''}>
                      <div className="text-3xl font-bold text-vermillion-600 tracking-tight leading-none">
                        {stat.value}
                      </div>
                      <div className="mt-1 text-xs font-medium text-gray-400 uppercase tracking-wide">
                        {stat.label}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Description */}
                <p className="mt-6 text-base leading-relaxed text-gray-500 border-t border-gray-100 pt-6">
                  {solution.description}
                </p>

                {/* Features */}
                <div className="mt-6 flex flex-col gap-3.5">
                  {solution.features.map((feature) => (
                    <div key={feature} className="flex gap-3">
                      <CheckIcon />
                      <span className="text-sm text-gray-700 leading-snug">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Learn more link */}
                {solution.learnMore && (
                  <div className="mt-6">
                    <Link
                      to={solution.learnMore}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-vermillion-600 hover:text-vermillion-700 transition-colors"
                    >
                      How it works
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M3 7h8M7 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </Link>
                  </div>
                )}

              </div>

              {/* Animation column */}
              <div className={`solution-image-container mt-10 lg:mt-0 lg:col-span-7${solution.isReversed ? ' lg:order-1' : ''}`}>
                <solution.Component />
              </div>
            </div>
          ))}
        </div>

      </Container>
    </section>
  );
};

export default Solutions;
